import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { practitionerService } from '../../services/practitionerService';

import Icon from '../../components/AppIcon';
import BrandingTab from './components/BrandingTab';
import ContactTab from './components/ContactTab';
import ReferralSettingsTab from './components/ReferralSettingsTab';
import AnalyticsTab from './components/AnalyticsTab';
import AccountTab from './components/AccountTab';
import Button from '../../components/ui/Button';


const PractitionerManagementDashboard = () => {
  const navigate = useNavigate();
  const { userProfile, loading: authLoading, signOut } = useAuth();
  
  const [activeTab, setActiveTab] = useState('branding');
  const [practitionerData, setPractitionerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Load practitioner data
  useEffect(() => {
    if (!authLoading && userProfile) {
      loadPractitionerData();
    }
  }, [authLoading, userProfile]);

  const loadPractitionerData = async () => {
    try {
      setLoading(true);
      setError('');

      // Check if user has practitioner access
      if (!userProfile?.practitionerId) {
        setError('Accès refusé : Aucun profil praticien associé');
        setLoading(false);
        return;
      }

      const data = await practitionerService?.getById(userProfile?.practitionerId);
      setPractitionerData(data);
    } catch (err) {
      console.error('Error loading practitioner data:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (updates) => {
    try {
      setSaveSuccess(false);
      setError('');

      const updatedData = await practitionerService?.update(
        userProfile?.practitionerId,
        updates
      );
      
      setPractitionerData(updatedData);
      setSaveSuccess(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving practitioner data:', err);
      setError('Erreur lors de la sauvegarde');
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/admin-login');
  };

  const tabs = [
    { id: 'branding', label: 'Identité visuelle', icon: 'Palette' },
    { id: 'contact', label: 'Coordonnées', icon: 'Mail' },
    { id: 'referral', label: 'Paramètres d\'adressage', icon: 'Link' },
    { id: 'analytics', label: 'Statistiques', icon: 'Activity' },
    { id: 'account', label: 'Compte', icon: 'User' }
  ];

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error && !practitionerData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Erreur</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/login')} variant="outline">
            Retour à la connexion
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Gestion du Profil - EndoReferral</title>
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Gestion du Profil</h1>
                <p className="text-sm text-gray-600">Configurez votre identité et vos paramètres</p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/practitioner-reports-dashboard')}
                  iconName="BarChart3"
                >
                  Rapports
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  iconName="LogOut"
                >
                  Déconnexion
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Success Message */}
          {saveSuccess && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
              <Icon name="CheckCircle" size={20} className="text-green-600" />
              <p className="text-sm text-green-800">Modifications enregistrées avec succès</p>
            </div>
          )}

          {/* Error Message */}
          {error && practitionerData && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <Icon name="AlertCircle" size={20} className="text-red-600" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden">
            <div className="border-b border-gray-200">
              <nav className="flex overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
                      activeTab === tab?.id
                        ? 'border-blue-600 text-blue-600 bg-blue-50/50' :'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Icon name={tab?.icon} size={18} />
                    {tab?.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'branding' && (
                <BrandingTab 
                  practitionerData={practitionerData}
                  onSave={handleSave}
                />
              )}
              {activeTab === 'contact' && (
                <ContactTab 
                  practitionerData={practitionerData}
                  onSave={handleSave}
                />
              )}
              {activeTab === 'referral' && (
                <ReferralSettingsTab 
                  practitionerData={practitionerData}
                  onSave={handleSave}
                />
              )}
              {activeTab === 'analytics' && (
                <AnalyticsTab 
                  practitionerId={userProfile?.practitionerId}
                />
              )}
              {activeTab === 'account' && (
                <AccountTab />
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default PractitionerManagementDashboard;